import React, {useEffect, useRef, useState, useMemo} from "react";
import {
    PersistedExtractionJob,
    SubtitleBurnInJob,
    SubtitleBurnInJobStatus,
    TranslationJob,
    TranslationJobStatus
} from "@/types";
import {getLanguageName} from "@/utils/textFormatting";
import Dropdown from "@/Components/Dropdown";
import ProgressBar from "@/Components/ProgressBar";
import {listenForBurnInJobUpdates, cancelBurnInJob, downloadOutput} from "@/services/subtitleBurnInJobManager";
import {listenForTranslationProgress} from "@/services/translate/AI/AiTranslateService";
import TranslateIcon from "@/Components/Icons/TranslateIcon";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFilm, faXmark} from "@fortawesome/free-solid-svg-icons";
import {Tooltip} from "flowbite-react";
import {ProgressListener} from "@/utils/progressListener";
import moment from "moment";
import {useConfirm} from "@/Components/ConfirmDialog";
import toast from "react-hot-toast";
import {Link} from "@inertiajs/react";

type TaskPanelProps = {
    extractionJob: PersistedExtractionJob;
    translationJobs: TranslationJob[];
    burnInJobs: SubtitleBurnInJob[];
    onTranslationComplete?: (job: TranslationJob, error?: Error) => void;
    onBurnInComplete?: (job: SubtitleBurnInJob, error?: Error) => void;
};

type TaskStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

type TranslationJobItem = {
    type: 'translation';
    job: TranslationJob;
    progress: number;
    status: TaskStatus;
};

type BurnInJobItem = {
    type: 'burn-in';
    job: SubtitleBurnInJob;
    progress: number;
    status: TaskStatus;
};

type Task = TranslationJobItem | BurnInJobItem;

function mapTranslationStatus(status: TranslationJobStatus): TaskStatus {
    if(status === 'refused') return 'failed';
    return status;
}

function mapBurnInStatus(status: SubtitleBurnInJobStatus): TaskStatus {
    switch (status) {
        case 'CANCELLED': return 'cancelled';
        case 'FAILED': return 'failed';
        case 'SUCCEEDED': return 'completed';
        case 'QUEUED': return 'pending';
        case 'RUNNING': return 'running';
    }
}

function handleDownload(job: SubtitleBurnInJob) {
    downloadOutput(job.id)
        .catch(err => toast.error(`Failed to download output. ${err.message}`));
}

export default function TaskPanel({
    extractionJob,
    translationJobs,
    burnInJobs,
    onTranslationComplete,
    onBurnInComplete,
}: TaskPanelProps) {
    const [tasks, setTasks] = useState<Task[]>([]);
    const listenersRef = useRef<Map<string, ProgressListener>>(new Map());
    const completedJobsRef = useRef<Set<string>>(new Set());
    const confirm = useConfirm()

    const handlersRef = useRef({ onTranslationComplete, onBurnInComplete });

    useEffect(() => {
        handlersRef.current = { onTranslationComplete, onBurnInComplete };
    }, [onTranslationComplete, onBurnInComplete]);

    useEffect(() => {
        return () => {
            listenersRef.current.forEach(listener => listener.disconnect());
            listenersRef.current.clear();
        };
    }, []);

    const removeListener = (id: string) => {
        listenersRef.current.get(id)?.disconnect();
        listenersRef.current.delete(id);
    }

    const handleJobCancellation = async (job: SubtitleBurnInJob) => {
        if(await confirm('Are you sure you want to cancel? Credits will not be refunded.')) {
            try {
                await cancelBurnInJob(job)
                removeListener(job.job_name)
                onBurnInComplete?.(job, new Error('cancelled'))
            } catch (err: any) {
                toast.error(`Failed to cancel burn-in job. ${err.message}`)
            }
        }
    }

    useEffect(() => {
        const nextTasks: Task[] = [];

        translationJobs.forEach(tj => {
            const status = mapTranslationStatus(tj.status);
            const item: TranslationJobItem = {
                type: 'translation',
                job: tj,
                status,
                progress: tj.status === 'completed' ? 100 : 0,
            };
            nextTasks.push(item);
        });

        burnInJobs.forEach(bj => {
            const status = mapBurnInStatus(bj.status);
            const item: BurnInJobItem = {
                type: 'burn-in',
                job: bj,
                status,
                progress: bj.status === 'SUCCEEDED' ? 100 : 0,
            };
            nextTasks.push(item);
        });

        setTasks(prev => {
            const prevMap = new Map(prev.map(j => [j.job.job_name, j]));
            return nextTasks.map(job => {
                const key = job.job.job_name;
                const prevJob = prevMap.get(key);
                if (prevJob && (job.status === 'pending' || job.status === 'running') && prevJob.status === 'running') {
                    return { ...job, status: 'running', progress: prevJob.progress };
                }
                return job;
            });
        });
    }, [translationJobs, burnInJobs]);

    const activeTasks = useMemo(() => {
        return tasks.filter(job =>
            job.status === 'pending' || job.status === 'running'
        );
    }, [tasks]);

    const recentTasks = useMemo(() => {
        return tasks.filter(job =>
                job.status === 'completed' || job.status === 'failed'
            )
            .sort((a, b) => moment(b.job.created_at).diff(a.job.created_at))
            .slice(0, 5);
    }, [tasks]);

    const latestTranslationByLang = useMemo(() => {
        const map = new Map<string, string>();
        [...translationJobs]
            .filter(tj => tj.status === 'completed')
            .sort((a, b) => moment(b.created_at).diff(a.created_at))
            .forEach(tj => {
                if (!map.has(tj.language)) {
                    map.set(tj.language, tj.job_name);
                }
            });
        return map;
    }, [translationJobs]);

    useEffect(() => {
        const activeTasksMap = new Map(activeTasks.map(t => [t.job.job_name, t]));

        listenersRef.current.forEach((listener, key) => {
            if (!activeTasksMap.has(key)) {
                listener.disconnect();
                listenersRef.current.delete(key);
            }
        });

        activeTasksMap.forEach((task, key) => {
            if (!listenersRef.current.has(key)) {
                const updateJob = (updates: Partial<Task>) => {
                    setTasks(prev => prev.map(j => j.job.job_name === key ? { ...j, ...updates } as Task : j));
                };

                const onComplete = () => {
                    if (!completedJobsRef.current.has(key)) {
                        completedJobsRef.current.add(key);
                        updateJob({ status: 'completed', progress: 100 });
                        if (task.type === 'translation') {
                            handlersRef.current.onTranslationComplete?.(task.job);
                        } else {
                            handlersRef.current.onBurnInComplete?.(task.job);
                        }
                    }
                    removeListener(key)
                };

                const onError = (err: Error) => {
                    if (!completedJobsRef.current.has(key)) {
                        updateJob({ status: 'failed', progress: 0 });
                        removeListener(key)
                        completedJobsRef.current.add(key);
                        if (task.type === 'translation') {
                            handlersRef.current.onTranslationComplete?.(task.job, err);
                        } else {
                            handlersRef.current.onBurnInComplete?.(task.job, err);
                        }
                    }
                };

                let listener;
                if (task.type === 'translation') {
                    listener = listenForTranslationProgress({
                        jobId: task.job.job_name,
                        videoDuration: extractionJob.file_duration,
                        startedAt: new Date(task.job.created_at).getTime(),
                        onUpdate: (p) => updateJob({ status: 'running', progress: p }),
                        onComplete,
                        onError,
                    });
                } else {
                    listener = listenForBurnInJobUpdates({
                        jobId: task.job.job_name,
                        startedAt: new Date(task.job.created_at).getTime(),
                        onUpdate: (p) => updateJob({ status: 'running', progress: p }),
                        onComplete,
                        onError,
                    });
                }
                listenersRef.current.set(key, listener);
            }
        });
    }, [activeTasks, extractionJob.file_duration]);

    const hasContent = activeTasks.length > 0 || recentTasks.length > 0;

    return (
        <div className={`transition-all duration-200 ${hasContent ? '' : 'w-0 overflow-hidden pointer-events-none'}`}>
        <Dropdown>
            <Dropdown.Trigger>
                <Tooltip theme={{target: `inline-flex me-2`}} content="Tasks" className="p-2 py-1 text-xs">
                <button
                    className={`relative inline-flex items-center justify-center px-2.5 py-2 text-sm font-medium text-gray-900 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 focus:outline-none focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 transition-all duration-200 ${hasContent ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                    title="Tasks"
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        preserveAspectRatio="xMidYMid meet"
                        fill="none"
                        role="presentation"
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-current"
                    >
                        <g>
                            <path
                                d="m20 10-1-2H5l-1 2h16Zm-2-4-1-2H7L6 6h12Z"
                                fill="currentColor"
                            />
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M3 12h18v6a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-6Zm2 2h14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-4Z"
                                fill="currentColor"
                            />
                        </g>
                    </svg>
                    {activeTasks.length > 0 && (
                        <span className="absolute -top-[0.5rem] -right-[0.4rem] flex h-4 w-4 ring-4 ring-white dark:ring-gray-800 rounded-full">
                            <span className="relative inline-flex  rounded-full h-4 w-4 bg-orange-500 text-white text-[10px] font-bold items-center justify-center">
                                {activeTasks.length}
                            </span>
                        </span>
                    )}
                </button>
                </Tooltip>
            </Dropdown.Trigger>
            <Dropdown.Content
                zIndex={50}
                align="right"
                width="lg"
                className="!w-80 sm:mt-1"
                contentClasses="py-0 bg-white dark:bg-gray-800"
            >
                <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Tasks
                    </h3>
                </div>
                <div className="max-h-80 overflow-y-auto">
                    {activeTasks.length > 0 && (
                        <div>
                            <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                                {activeTasks.length} {activeTasks.length === 1 ? 'task' : 'tasks'} in progress
                            </div>
                            {activeTasks.map(task => (
                                <TaskItem
                                    key={task.job.job_name}
                                    job={task}
                                    onCancel={task.type === 'burn-in' ? () => handleJobCancellation(task.job) : undefined}
                                    showViewLink={task.type === 'translation' &&
                                        latestTranslationByLang.get(task.job.language) === task.job.job_name &&
                                        route().params.language !== task.job.language
                                    }
                                />
                            ))}
                        </div>
                    )}
                    {recentTasks.length > 0 && (
                        <div className={activeTasks.length > 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''}>
                            <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                                Completed tasks
                            </div>
                            {recentTasks.map(task => (
                                <TaskItem
                                    key={task.job.job_name}
                                    job={task}
                                    showViewLink={task.type === 'translation' && latestTranslationByLang.get(task.job.language) === task.job.job_name && route().params.language !== task.job.language}
                                />
                            ))}
                        </div>
                    )}
                    {activeTasks.length === 0 && recentTasks.length === 0 && (
                        <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                            No tasks
                        </div>
                    )}
                </div>
            </Dropdown.Content>
        </Dropdown>
        </div>
    );
}

function TaskItem({job, onCancel, showViewLink}: {job: Task, onCancel?: (task: Task) => void, showViewLink?: boolean }) {
    const isActive = job.status === 'pending' || job.status === 'running';
    const isCompleted = job.status === 'completed';

    const isExpired = useMemo(() => {
        if (job.type !== 'burn-in' || !isCompleted) return false;
        const burnInJob = job.job as SubtitleBurnInJob;
        return !burnInJob.output || moment(burnInJob.output.expires_at).isBefore(moment());
    }, [job, isCompleted]);

    const getJobTitle = () => {
        const languageName = getLanguageName(job.job.language);
        if (job.type === 'translation') {
            return `${languageName} Translation`;
        }
        return `${languageName} Burn-in`;
    };

    const getJobIcon = () => {
        if (job.type === 'translation') {
            return (
                <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <TranslateIcon className="w-6 h-6 !me-0 text-blue-600 dark:text-blue-400" />
                </div>
            );
        }
        return (
            <div className="w-12 h-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                <FontAwesomeIcon icon={faFilm} className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
        );
    };

    return (
        <div className="px-3 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <div className="flex items-center gap-3">
                {getJobIcon()}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {getJobTitle()}
                        </h4>
                        {job.status === 'failed' && (
                            <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 rounded">
                                Failed
                            </span>
                        )}
                        {onCancel && (
                            <button
                                onClick={() => onCancel(job)}
                                className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                title="Cancel"
                            >
                                <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    {isActive && (
                        <>
                            <ProgressBar
                                progress={job.progress}
                                height="xs"
                                color="blue"
                                max={100}
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {job.progress.toFixed(0)}%
                            </p>
                        </>
                    )}
                    {!isActive && (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                            {moment(job.job.created_at).format('MMM DD, YYYY h:mm A')}
                        </p>
                    )}
                </div>
                {isCompleted && showViewLink && job.type === 'translation' && (
                    <Link
                        href={route('subtitle.edit', {id: job.job.extraction_job_id}) + `?language=${job.job.language}`}
                        className="flex-shrink-0 me-1 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5"
                             stroke="currentColor" className="size-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/>
                        </svg>

                    </Link>
                )}
                {isCompleted && job.type === 'burn-in' && (
                    <Tooltip
                        content={"Expired"}
                        className={`p-2 py-1 text-xs ${!isExpired ? 'hidden' : ''}`}
                    >
                        <button
                            onClick={() => !isExpired && handleDownload(job.job)}
                            disabled={isExpired}
                            className={`flex-shrink-0 me-1 ${
                                isExpired
                                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                    : 'text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 cursor-pointer'
                            }`}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                                />
                            </svg>
                        </button>
                    </Tooltip>
                )}
            </div>
        </div>
    );
}
